import { Injectable, signal } from '@angular/core';

export type ChatWindowMode = 'small' | 'minimized' | 'expanded';

export interface ChatWindowState {
  conversationId: Guid;
  mode: ChatWindowMode;
}

interface PersistedChatDock {
  isFlyoutOpen: boolean;
  windows: ChatWindowState[];
}

const MAX_SMALL_WINDOWS = 3;
const STORAGE_KEY_PREFIX = 'admin_chatDock_';
const VALID_MODES = new Set<ChatWindowMode>(['small', 'minimized', 'expanded']);

@Injectable({ providedIn: 'root' })
export class ChatDockStore {
  private readonly _isFlyoutOpen = signal(false);
  private readonly _windows = signal<ChatWindowState[]>([]);
  private currentFacilityId: Guid | null = null;
  private hasRestoredSinceLoad = false;

  readonly isFlyoutOpen = this._isFlyoutOpen.asReadonly();
  readonly windows = this._windows.asReadonly();

  toggleFlyout(): void {
    this._isFlyoutOpen.update((isOpen) => !isOpen);
    this.persist();
  }

  openWindow(conversationId: Guid): void {
    const others = this._windows().filter((window) => window.conversationId !== conversationId);
    const next = [...others, { conversationId: conversationId, mode: 'small' as const }];

    this._windows.set(this.enforceSmallLimit(next, conversationId));
    this.persist();
  }

  setWindowMode(conversationId: Guid, mode: ChatWindowMode): void {
    const next = this._windows().map((window) => {
      if (window.conversationId === conversationId) {
        return { ...window, mode: mode };
      }

      if (mode === 'expanded' && window.mode === 'expanded') {
        return { ...window, mode: 'small' as const };
      }

      return window;
    });

    this._windows.set(this.enforceSmallLimit(next, conversationId));
    this.persist();
  }

  closeWindow(conversationId: Guid): void {
    this._windows.update((windows) => windows.filter((window) => window.conversationId !== conversationId));
    this.persist();
  }

  restoreForFacility(facilityId: Guid | null): void {
    this.currentFacilityId = facilityId;
    this._isFlyoutOpen.set(false);
    this._windows.set([]);

    if (facilityId === null) {
      return;
    }

    const persisted = this.readPersisted(facilityId);
    const isFirstRestore = !this.hasRestoredSinceLoad;

    this.hasRestoredSinceLoad = true;

    if (!persisted) {
      return;
    }

    if (isFirstRestore) {
      this.restoreMinimized(persisted.windows);

      return;
    }

    this._isFlyoutOpen.set(persisted.isFlyoutOpen);
    this._windows.set(persisted.windows);
  }

  clearForFacilityChange(): void {
    this._windows.set([]);
    this._isFlyoutOpen.set(false);
  }

  retainWindows(conversationIds: Guid[]): void {
    const current = this._windows();
    const retained = current.filter((window) => conversationIds.includes(window.conversationId));

    if (retained.length === current.length) {
      return;
    }

    this._windows.set(retained);
    this.persist();
  }

  removeAllPersisted(): void {
    try {
      const dockKeys = Object.keys(localStorage).filter((key) => key.startsWith(STORAGE_KEY_PREFIX));

      dockKeys.forEach((key) => localStorage.removeItem(key));
    } catch {
      return;
    }
  }

  private restoreMinimized(windows: ChatWindowState[]): void {
    const minimized = windows.map((window) => ({ ...window, mode: 'minimized' as const }));

    this._windows.set(minimized);
    this.persist();
  }

  private enforceSmallLimit(windows: ChatWindowState[], keepConversationId: Guid): ChatWindowState[] {
    const result = [...windows];
    let smallCount = result.filter((window) => window.mode === 'small').length;

    for (let index = 0; index < result.length && smallCount > MAX_SMALL_WINDOWS; index++) {
      if (result[index].mode === 'small' && result[index].conversationId !== keepConversationId) {
        result[index] = { ...result[index], mode: 'minimized' };
        smallCount--;
      }
    }

    return result;
  }

  private storageKey(facilityId: Guid): string {
    return `${STORAGE_KEY_PREFIX}${facilityId}`;
  }

  private persist(): void {
    if (this.currentFacilityId === null) {
      return;
    }

    const payload: PersistedChatDock = { isFlyoutOpen: this._isFlyoutOpen(), windows: this._windows() };

    try {
      localStorage.setItem(this.storageKey(this.currentFacilityId), JSON.stringify(payload));
    } catch {
      return;
    }
  }

  private readPersisted(facilityId: Guid): PersistedChatDock | null {
    try {
      const raw = localStorage.getItem(this.storageKey(facilityId));

      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw);
      const sanitized = this.sanitize(parsed);

      return sanitized;
    } catch {
      return null;
    }
  }

  private sanitize(value: Partial<PersistedChatDock> | null): PersistedChatDock | null {
    if (!value || !Array.isArray(value.windows)) {
      return null;
    }

    const windows = value.windows.filter(
      (window) => typeof window?.conversationId === 'string' && VALID_MODES.has(window.mode)
    );

    const isFlyoutOpen = value.isFlyoutOpen === true;

    return { isFlyoutOpen: isFlyoutOpen, windows: windows };
  }
}
